import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  removeQuestionFromTemplateOptimistic,
  changeQuestionOrderOptimistic,
} from "../../dvirSlice";
import { DVIRTemplateQuestion, DVIRQuestionType } from "../../../../models";
import "./TemplateQuestionsManager.scss";

interface TemplateQuestionsManagerProps {
  templateId: string;
  questions: DVIRTemplateQuestion[];
}

const getQuestionTypeLabel = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "Boolean";
    case "text":
      return "Text";
    case "number":
      return "Number";
    default:
      return type;
  }
};

const getQuestionTypeColor = (type: DVIRQuestionType): string => {
  switch (type) {
    case "boolean":
      return "type-boolean";
    case "text":
      return "type-text";
    case "number":
      return "type-number";
    default:
      return "type-boolean";
  }
};

export function TemplateQuestionsManager({
  templateId,
  questions,
}: TemplateQuestionsManagerProps) {
  const dispatch = useAppDispatch();
  const [draggedItem, setDraggedItem] = useState<DVIRTemplateQuestion | null>(
    null
  );
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const removeQuestionAPI = useAppSelector(
    (state) => state.dvir.removeQuestionFromTemplate
  );
  const changeOrderAPI = useAppSelector(
    (state) => state.dvir.changeQuestionOrder
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    question: DVIRTemplateQuestion
  ) => {
    setDraggedItem(question);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
    (e.currentTarget as HTMLElement).style.opacity = "0.5";
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLElement).style.opacity = "1";
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem) return;

    const dragIndex = questions.findIndex((q) => q.id === draggedItem.id);

    if (dragIndex !== dropIndex) {
      // Calculate new order position (1-based)
      const newOrderPosition = dropIndex + 1;

      dispatch(
        changeQuestionOrderOptimistic({
          templateId,
          questionId: draggedItem.id,
          newOrderPosition,
        })
      );
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleRemoveQuestion = (question: DVIRTemplateQuestion) => {
    dispatch(
      removeQuestionFromTemplateOptimistic({
        templateId,
        questionId: question.id,
      })
    );
  };

  if (questions.length === 0) {
    return (
      <div className="template-questions-manager empty">
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h4>No questions in this template</h4>
          <p>
            Add questions to this template to create your DVIR inspection
            checklist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="template-questions-manager">
      <div className="questions-list">
        {questions.map((question, index) => (
          <div
            key={question.id}
            className={`question-item ${
              dragOverIndex === index ? "drag-over" : ""
            }`}
            draggable
            onDragStart={(e) => handleDragStart(e, question)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="question-content">
              <div className="question-header">
                <div className="question-order">#{question.orderPosition}</div>
                <div className="question-text">{question.questionText}</div>
                {question.isRequired && (
                  <span className="required-badge">Required</span>
                )}
              </div>

              <div className="question-meta">
                <span
                  className={`question-type-badge ${getQuestionTypeColor(
                    question.questionType
                  )}`}
                >
                  {getQuestionTypeLabel(question.questionType)}
                </span>
              </div>
            </div>

            <div className="question-actions">
              <button
                className="remove-btn"
                onClick={() => handleRemoveQuestion(question)}
                disabled={removeQuestionAPI.loading}
                title="Remove question from template"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {(removeQuestionAPI.loading || changeOrderAPI.loading) && (
        <div className="loading-overlay">
          <div className="loading-spinner">Updating...</div>
        </div>
      )}
    </div>
  );
}
